/**
 * Context-sensitive help content for all pages
 * Each page has: title, subtitle, overview, sections, and related docs
 */

const helpContent = {
  // Dashboard page
  dashboard: {
    title: 'Dashboard Help',
    subtitle: 'Understanding your dashboard',
    overview: 'Your dashboard provides a quick overview of verification activity, usage statistics, and important metrics.',
    sections: [
      {
        title: 'Validation Limits',
        content: 'Track your monthly verification usage against your plan limits.',
        tips: [
          'Usage resets on the 1st of each month',
          'Upgrade your plan if you consistently hit limits',
          'Set up email alerts at 80% and 90% usage'
        ]
      },
      {
        title: 'Recent Activity',
        content: 'View the latest verification requests and their status.',
        steps: [
          'Click on any request to view details',
          'Use filters to find specific verifications',
          'Export data for reporting'
        ]
      },
      {
        title: 'Quick Actions',
        content: 'Common tasks you can perform directly from the dashboard.',
        tips: [
          'Create new verification requests',
          'View pending approvals',
          'Access frequently used reports'
        ]
      }
    ],
    relatedDocs: [
      { title: 'Admin Guide', url: '/docs/admin-guide' },
      { title: 'Getting Started', url: '/docs/getting-started' }
    ]
  },

  // API page
  api: {
    title: 'API Integration Help',
    subtitle: 'Programmatic access to Acquiva',
    overview: 'Use API tokens to integrate Acquiva verification into your applications programmatically.',
    sections: [
      {
        title: 'Generating API Tokens',
        content: 'Create secure API tokens for your applications.',
        steps: [
          'Click "Generate New Token"',
          'Provide a descriptive name (e.g., "Production Server")',
          'Copy the token immediately - it\'s shown only once',
          'Store the token securely in your environment variables'
        ],
        tips: [
          'Use different tokens for dev, staging, and production',
          'Rotate tokens every 90 days for security',
          'Never commit tokens to version control'
        ]
      },
      {
        title: 'Using API Tokens',
        content: 'Authenticate your API requests with tokens.',
        example: `curl -X POST https://api.acquiva.com/api/v1/verification-requests \\
  -H "Authorization: Bearer YOUR_API_TOKEN" \\
  -H "X-Encryption-Key: YOUR_ENCRYPTION_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"first_name": "John", "last_name": "Smith"}'`,
        tips: [
          'Include both Authorization and X-Encryption-Key headers',
          'Handle rate limits gracefully',
          'Log API responses for debugging'
        ]
      },
      {
        title: 'Security Best Practices',
        content: 'Keep your API integration secure.',
        tips: [
          'Store tokens in environment variables, not code',
          'Use HTTPS for all API calls',
          'Implement proper error handling',
          'Monitor token usage for suspicious activity',
          'Revoke compromised tokens immediately'
        ]
      }
    ],
    relatedDocs: [
      { title: 'API Documentation', url: '/docs/api-reference' },
      { title: 'Authentication Guide', url: '/docs/authentication' },
      { title: 'Code Examples', url: '/docs/examples' }
    ]
  },

  // Encryption page
  encryption: {
    title: 'Data Encryption Help',
    subtitle: 'Protecting your data',
    overview: 'Client-side encryption ensures your data is secure. Only you have the encryption key - we cannot access your data.',
    sections: [
      {
        title: '⚠️ Critical: Encryption Key',
        content: 'Your encryption key is shown ONLY ONCE when generated. You must save it securely.',
        steps: [
          'Click "Generate Encryption Key"',
          'Copy the 64-character key immediately',
          'Store in a password manager (1Password, LastPass, Bitwarden)',
          'Create a backup in a secure vault',
          'NEVER store in plain text files or emails'
        ],
        tips: [
          'Without your key, your data is permanently inaccessible',
          'Even Acquiva staff cannot decrypt your data',
          'Treat this key like a master password'
        ]
      },
      {
        title: 'Using Your Encryption Key',
        content: 'Include your encryption key in all API requests.',
        example: `// JavaScript example
const response = await fetch('/api/v1/verification-requests', {
  headers: {
    'Authorization': 'Bearer YOUR_API_TOKEN',
    'X-Encryption-Key': 'YOUR_64_CHARACTER_KEY',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({ first_name: 'John', last_name: 'Smith' })
});`,
        tips: [
          'Store key in environment variables',
          'Never log or display the key',
          'Verify key is correct before bulk operations'
        ]
      },
      {
        title: 'What Gets Encrypted?',
        content: 'All sensitive personal data is encrypted with your key.',
        tips: [
          '✅ Names, emails, phone numbers',
          '✅ Addresses and dates of birth',
          '✅ Document photos and videos',
          '✅ Verification metadata',
          '❌ Non-sensitive data (IDs, timestamps, status)'
        ]
      }
    ],
    relatedDocs: [
      { title: 'Encryption Guide', url: '/docs/encryption' },
      { title: 'Security Best Practices', url: '/docs/security' }
    ]
  },

  // Verification Sources page
  verificationSources: {
    title: 'Verification Sources Help',
    subtitle: 'Configure verification methods',
    overview: 'Enable and configure different verification sources based on your needs. Each source has different costs and confidence levels.',
    sections: [
      {
        title: 'Understanding Confidence Scores',
        content: 'Each verification source provides a confidence score indicating reliability.',
        tips: [
          '97%+ = Airport-grade verification (highest)',
          '85-96% = High confidence (government databases)',
          '70-84% = Medium confidence (OCR + AI)',
          '60-69% = Lower confidence (social profiles)',
          'Combine multiple sources for higher confidence'
        ]
      },
      {
        title: 'Cost vs Confidence',
        content: 'Balance verification costs with required confidence levels.',
        example: `Passport Document Scan: £0.60, 75% confidence
Passport Airport-Grade: £3.50, 97% confidence

For standard KYC: Use Document Scan
For regulated industries: Use Airport-Grade`,
        tips: [
          'Use cheaper methods for low-risk verifications',
          'Combine multiple cheap sources instead of one expensive',
          'Monitor costs vs fraud prevention savings'
        ]
      }
    ],
    relatedDocs: [
      { title: 'Verification Methods', url: '/docs/verification-methods' },
      { title: 'Pricing Guide', url: '/docs/pricing' }
    ]
  },

  // Clients page
  clients: {
    title: 'Client Management Help',
    subtitle: 'Managing your clients',
    overview: 'View and manage all clients who have verification requests with your company.',
    sections: [
      {
        title: 'Creating Verification Requests',
        content: 'Start a new identity verification for a client.',
        steps: [
          'Click "New Verification Request"',
          'Enter client details (name, email, phone)',
          'Select required verification sources',
          'Choose verification level',
          'Send request to client'
        ],
        tips: [
          'Client receives email/SMS with verification link',
          'Track progress in real-time',
          'Set expiry dates for time-sensitive verifications'
        ]
      },
      {
        title: 'Viewing Client Files',
        content: 'Access complete verification history for each client.',
        tips: [
          'Click on client name to view full file',
          'See all verification requests and results',
          'Download documents and reports',
          'Add notes and comments'
        ]
      }
    ],
    relatedDocs: [
      { title: 'Client Management Guide', url: '/docs/clients' }
    ]
  },

  // Platform Settings page
  platformSettings: {
    title: 'Platform Settings Help',
    subtitle: 'Configure platform-wide settings',
    overview: 'Platform administrators can configure global settings that affect all companies.',
    sections: [
      {
        title: 'OpenAI API Key',
        content: 'Configure OpenAI integration for AI-powered verification.',
        steps: [
          'Get API key from OpenAI (https://platform.openai.com)',
          'Add setting: key = "openai_api_key"',
          'Paste your API key (starts with "sk-")',
          'Save settings'
        ],
        tips: [
          'This enables: Passport OCR, Video Analysis, Face Comparison',
          'Monitor OpenAI usage and costs',
          'Keep API key secure - never share publicly'
        ]
      },
      {
        title: 'Managing Settings',
        content: 'Add, edit, and delete platform settings.',
        tips: [
          'Settings are key-value pairs',
          'Use descriptive keys (e.g., "max_file_size_mb")',
          'Document what each setting does',
          'Test changes in staging first'
        ]
      }
    ],
    relatedDocs: [
      { title: 'Platform Admin Guide', url: '/docs/platform-admin' },
      { title: 'OpenAI Integration', url: '/docs/openai' }
    ]
  },

  // Video Selfie page
  videoSelfie: {
    title: 'Video Selfie Help',
    subtitle: 'Biometric video verification',
    overview: 'Video selfie verification uses AI to confirm the person is real and matches their ID documents.',
    sections: [
      {
        title: 'Three Verification Tiers',
        content: 'Choose the right level of verification for your needs.',
        example: `Basic Video Selfie (£0.40, 70%)
- Records short video
- Compares with document photos
- Best for: Basic identity confirmation

Video Selfie + Liveness (£0.80, 85%)
- Active liveness detection
- Random challenges (blink, smile, turn head)
- Anti-spoofing protection
- Best for: Fraud prevention

Video Declaration (£1.20, 92%)
- User reads specific declaration
- Speech-to-text verification
- Legal consent recording
- Best for: Contracts, high-value transactions`,
        tips: [
          'Liveness detection prevents photo/video spoofing',
          'Declarations create legal evidence',
          'Combine with document verification for 95%+ confidence'
        ]
      },
      {
        title: 'Multi-Document Face Matching',
        content: 'Compare video with multiple documents for higher confidence.',
        tips: [
          'Government IDs (passport, license) have highest weight',
          'LinkedIn profiles have medium weight',
          'Social media has lowest weight',
          '3+ matching documents = +10% confidence boost',
          '2+ government IDs matching = +5% extra boost'
        ]
      }
    ],
    relatedDocs: [
      { title: 'Video Verification Guide', url: '/docs/video-verification' },
      { title: 'Face Matching Algorithm', url: '/docs/face-matching' }
    ]
  },

  // Reports page
  reports: {
    title: 'Reports & Analytics Help',
    subtitle: 'Track verification metrics',
    overview: 'Generate reports and analyze verification data to understand usage patterns and costs.',
    sections: [
      {
        title: 'Available Reports',
        content: 'Different report types for various needs.',
        tips: [
          'Verification Summary: Overall stats and trends',
          'Cost Analysis: Spending by verification source',
          'Confidence Scores: Success rates and accuracy',
          'Client Activity: Per-client verification history',
          'Compliance Reports: Audit trails and records'
        ]
      },
      {
        title: 'Exporting Data',
        content: 'Download reports in various formats.',
        steps: [
          'Select report type',
          'Choose date range',
          'Apply filters (client, source, status)',
          'Select format (PDF, CSV, Excel)',
          'Click "Generate Report"'
        ],
        tips: [
          'CSV for data analysis in Excel',
          'PDF for sharing with stakeholders',
          'Schedule automated reports (coming soon)'
        ]
      }
    ],
    relatedDocs: [
      { title: 'Reporting Guide', url: '/docs/reports' }
    ]
  },

  // Client View - My Verification page
  myVerification: {
    title: 'Sharing Your Verification',
    subtitle: 'Share your identity verification securely',
    overview: 'Create secure sharing tokens to share your verified identity with companies and organizations.',
    sections: [
      {
        title: 'What is a Sharing Token?',
        content: 'A sharing token is a secure, time-limited link that allows you to share your verified identity.',
        tips: [
          'Each token is unique and can only be used once',
          'You control who can access your information',
          'Tokens expire after a set time for security',
          'You can revoke access at any time'
        ]
      },
      {
        title: 'Creating a Sharing Token',
        content: 'Share your verification with a company or organization.',
        steps: [
          'Click "Create New Sharing Token"',
          'Enter the company or person\'s name',
          'Choose how long the token should be valid',
          'Click "Generate Token"',
          'Copy the link and send it to the recipient'
        ],
        tips: [
          'Use shorter expiry times for better security',
          'Only share tokens with trusted organizations',
          'Keep track of who you\'ve shared with'
        ]
      },
      {
        title: 'Managing Your Tokens',
        content: 'View and control your active sharing tokens.',
        tips: [
          'See all active tokens in one place',
          'Check when each token was created and when it expires',
          'Revoke a token if you no longer want to share access',
          'View which tokens have been used'
        ]
      }
    ],
    relatedDocs: [
      { title: 'User Help Guide', url: '/docs/user-help' }
    ]
  },

  // Client View - My Documents page
  myDocuments: {
    title: 'Your Verification Documents',
    subtitle: 'View your verification history',
    overview: 'See all verification requests that have been made for your identity.',
    sections: [
      {
        title: 'Understanding Verification Requests',
        content: 'Companies may request verification of your identity for various purposes.',
        tips: [
          'Each request shows who requested it and when',
          'You can see what information was verified',
          'Completed verifications show the verification date',
          'Pending requests need your action'
        ]
      },
      {
        title: 'Viewing Request Details',
        content: 'Click on any request to see full details.',
        tips: [
          'See what documents were submitted',
          'View verification status and results',
          'Check which company requested verification',
          'Download copies of your submitted documents'
        ]
      },
      {
        title: 'Your Privacy',
        content: 'Your data is encrypted and secure.',
        tips: [
          'Only you and authorized companies can see your data',
          'All documents are encrypted',
          'You can request deletion of old verifications',
          'Companies cannot share your data without permission'
        ]
      }
    ],
    relatedDocs: [
      { title: 'User Help Guide', url: '/docs/user-help' }
    ]
  },

  // Client View - My Profile page
  myProfile: {
    title: 'Your Profile',
    subtitle: 'Manage your account information',
    overview: 'Update your personal information and account settings.',
    sections: [
      {
        title: 'Updating Your Information',
        content: 'Keep your profile information current.',
        steps: [
          'Click "Edit Profile"',
          'Update your name, email, or phone number',
          'Update your address or date of birth if needed',
          'Click "Save Changes"'
        ],
        tips: [
          'Use a valid email address for notifications',
          'Keep your phone number current for SMS alerts',
          'Accurate information helps with verification'
        ]
      },
      {
        title: 'Account Security',
        content: 'Protect your account with strong security.',
        tips: [
          'Use a strong, unique password',
          'Enable two-factor authentication (2FA)',
          'Never share your password with anyone',
          'Log out when using shared computers'
        ]
      },
      {
        title: 'Changing Your Password',
        content: 'Update your password regularly for security.',
        steps: [
          'Click "Change Password"',
          'Enter your current password',
          'Enter your new password (must be strong)',
          'Confirm your new password',
          'Click "Update Password"'
        ],
        tips: [
          'Use at least 12 characters',
          'Include uppercase, lowercase, numbers, and symbols',
          'Don\'t reuse passwords from other sites'
        ]
      }
    ],
    relatedDocs: [
      { title: 'User Help Guide', url: '/docs/user-help' }
    ]
  },

  // KYB Sessions page
  kybSessions: {
    title: 'KYB Verification Sessions',
    subtitle: 'Business verification with AI assistance',
    overview: 'Complete Know Your Business (KYB) verification with intelligent AI-powered assistance.',
    sections: [
      {
        title: 'How AI Helps You',
        content: 'Our AI Help Assistant is available on every question to help you understand what\'s being asked.',
        tips: [
          '🤖 Click "Ask AI for Help" on any question',
          'The AI explains terminology and requirements',
          'It helps clarify what information is needed',
          'The AI cannot provide answers or fill in forms for you',
          'All AI conversations are recorded for audit purposes'
        ]
      },
      {
        title: 'AI Safety & Security',
        content: 'The AI is designed with strong protections to ensure fair verification.',
        tips: [
          'The AI will never tell you the "correct" answer',
          'It cannot be tricked into bypassing verification',
          'Attempts to manipulate the AI will flag your application for manual review',
          'The AI helps you understand, not cheat the system'
        ]
      },
      {
        title: 'AI Risk Manager',
        content: 'For applications that need additional review, our AI Risk Manager provides intelligent analysis.',
        tips: [
          'Reviews applications that weren\'t automatically approved',
          'Asks clarifying questions about concerning areas',
          'Can recommend additional verification steps',
          'All conversations are recorded and reviewed by compliance teams'
        ]
      },
      {
        title: 'Completing Your Verification',
        content: 'Follow the decision tree to complete your business verification.',
        steps: [
          'Answer each question honestly and completely',
          'Use AI Help if you need clarification',
          'Provide accurate business information',
          'Upload required documents when requested',
          'Review your answers before final submission'
        ]
      }
    ],
    relatedDocs: [
      { title: 'Admin Guide', url: '/docs/admin-guide' },
      { title: 'Technical Documentation', url: '/docs/technical' }
    ]
  },

  // Default fallback
  default: {
    title: 'Help & Support',
    subtitle: 'We\'re here to help',
    overview: 'Get help with using Acquiva\'s identity verification platform, powered by intelligent AI assistance.',
    sections: [
      {
        title: 'Getting Started',
        content: 'New to Acquiva? Start here.',
        steps: [
          'Configure your company profile',
          'Generate your encryption key',
          'Enable verification sources',
          'Create your first verification request',
          'Set up API access (optional)'
        ]
      },
      {
        title: 'AI-Powered Features',
        content: 'Acquiva uses AI to enhance verification while maintaining security.',
        tips: [
          '🤖 AI Help Assistant: Get help understanding verification questions',
          '🔍 AI Risk Manager: Intelligent review of complex applications',
          '🛡️ Fraud Detection: AI monitors for manipulation attempts',
          '📊 Smart Analysis: AI helps identify risk factors',
          'All AI interactions are logged for compliance and audit'
        ]
      },
      {
        title: 'How AI is Used Safely',
        content: 'Our AI is designed with strong ethical guidelines.',
        tips: [
          'AI explains but never provides answers',
          'Cannot be manipulated to bypass verification',
          'All AI decisions can be reviewed by humans',
          'Sensitive data is sanitized before AI processing',
          'AI recommendations are always reviewed by compliance teams'
        ]
      },
      {
        title: 'Common Tasks',
        content: 'Frequently performed actions.',
        tips: [
          'Create verification requests from Dashboard',
          'View client files in Clients section',
          'Generate API tokens in API section',
          'Configure sources in Settings',
          'Download reports from Reports section'
        ]
      },
      {
        title: 'Contact Support',
        content: 'Get help from our team.',
        tips: [
          '📧 Email: support@acquiva.com',
          '💬 Live Chat: Available 9am-5pm GMT',
          '📚 Documentation: /docs',
          '🔒 Security Issues: security@acquiva.com'
        ]
      }
    ],
    relatedDocs: [
      { title: 'Admin Guide', url: '/docs/admin-guide' },
      { title: 'User Help Guide', url: '/docs/user-help' },
      { title: 'Technical Documentation', url: '/docs/technical' }
    ]
  }
};

export default helpContent;

